import './add.scss';
import { FormEvent, useState } from 'react';
import { message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal } from 'components';
import { buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
import { Select } from 'components/select/select';
import { Enum } from 'core/domain';
import { deleteCommaSperator, digitSperator, toEnDigits } from 'core/utils/decimalExtentions';
import { addBalancerTransaction } from 'core/repositories/balancer-transaction';

interface Props {
    isOpen: boolean;
    orderSides: Enum[];
    onClose: () => void;
    onSuccess: () => void;
}

export const AddBalancerTransactionModal = ({ isOpen, orderSides, onClose, onSuccess }: Props) => {
    const [submitting, setSubmitting] = useState(false);

    return (
        <Modal
            title="افزودن تراکنش تأمین موجودی"
            isOpen={isOpen}
            setIsOpen={() => {
                if (!submitting) onClose();
            }}
            className="pmx-add-balancer-transaction"
        >
            <AddBalancerTransactionForm
                orderSides={orderSides}
                submitting={submitting}
                setSubmitting={setSubmitting}
                onClose={onClose}
                onSuccess={onSuccess}
            />
        </Modal>
    );
};

interface FormProps extends Pick<Props, 'orderSides' | 'onClose' | 'onSuccess'> {
    submitting: boolean;
    setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddBalancerTransactionForm = ({ orderSides, submitting, setSubmitting, onClose, onSuccess }: FormProps) => {
    const [side, setSide] = useState(1);
    const [symbol, setSymbol] = useState<string>();
    const [price, setPrice] = useState('');
    const [quantity, setQuantity] = useState('');

    const onPrice = Number(price);
    const amount = Number(quantity);
    const validPrice = Number.isFinite(onPrice) && onPrice > 0;
    const validAmount = Number.isFinite(amount) && amount > 0;
    const canSubmit = Boolean(symbol) && orderSides.some((item) => item.id === side) && validPrice && validAmount;

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (submitting || !canSubmit || !symbol) return;

        try {
            await addBalancerTransaction(setSubmitting, { onPrice, amount, side, symbol }, () => {
                message.success('تراکنش با موفقیت افزوده شد.');
                onSuccess();
            });
        } catch {
            message.error('خطا در ثبت تراکنش');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form dir="rtl" onSubmit={handleSubmit}>
            <p className="add-transaction-description">نوع معامله، ارز، قیمت و مقدار را وارد کنید.</p>

            <fieldset className="add-transaction-fields" disabled={submitting}>
                <div className="add-transaction-field">
                    <label htmlFor="balancer-side">نوع معامله</label>
                    <Select
                        id="balancer-side"
                        disabled={submitting}
                        value={side}
                        onChange={setSide}
                        options={orderSides
                            .filter((item) => item.id !== -1)
                            .map((item) => ({
                                value: item.id,
                                label: item.title,
                            }))}
                    />
                </div>
                <div className="add-transaction-field">
                    <label htmlFor="balancer-symbol">ارز</label>
                    <Select
                        id="balancer-symbol"
                        disabled={submitting}
                        value={symbol}
                        onChange={setSymbol}
                        placeholder="انتخاب ارز"
                        options={[
                            { value: 'UUSD', label: 'UUSD (یوتوپیا)' },
                            { value: 'USDT', label: 'USDT (تتر)' },
                        ]}
                    />
                </div>
                <Input
                    value={digitSperator(price)}
                    onChange={(event) => setPrice(deleteCommaSperator(toEnDigits(event.target.value)))}
                    label="قیمت"
                    placeholder="قیمت معامله"
                    inputMode="decimal"
                    dir="ltr"
                    error={price && !validPrice ? 'قیمت باید عددی بزرگ‌تر از صفر باشد.' : undefined}
                />
                <Input
                    value={digitSperator(quantity)}
                    onChange={(event) => setQuantity(deleteCommaSperator(toEnDigits(event.target.value)))}
                    label="مقدار"
                    placeholder="مقدار ارز"
                    inputMode="decimal"
                    dir="ltr"
                    error={quantity && !validAmount ? 'مقدار باید عددی بزرگ‌تر از صفر باشد.' : undefined}
                />
            </fieldset>

            <div className="add-transaction-actions">
                <Button
                    htmlType="submit"
                    type={buttonType.info}
                    loading={submitting}
                    disabled={!canSubmit}
                    icon={<PlusOutlined />}
                    text="ثبت تراکنش"
                />
                <Button
                    htmlType="button"
                    type={buttonType.outlined}
                    disabled={submitting}
                    onClick={onClose}
                    text="انصراف"
                />
            </div>
        </form>
    );
};
